import { Component, inject } from '@angular/core';
import { HlAreaComponent } from './hl-area.component';
import { DataService } from 'src/app/data.service';
import { ProjectService } from 'src/app/project.service';
import { AuthService } from 'src/app/auth.service';
import { idOf, istPunktartMitProtokoll, punktartDisplayLabel, punktartNummerFromLabel } from 'src/app/data-model';
import { applyKartiererOwner, kartiererOptionsFromProject } from '../../batch-edit/kartierer-ownership';
import { LetzteNummer, letzteNummernAusIndex } from './letzte-nummern';

@Component({
    standalone: false,
    selector: 'bp-aufnahme',
    templateUrl: './aufnahme.component.html',
    styleUrls: ['./hl-area.component.css', './aufnahme.component.css'],

})
export class AufnahmeComponent extends HlAreaComponent {
  ds: DataService = inject(DataService);
  private projectService = inject(ProjectService);
  private auth = inject(AuthService);

  /** Meine zuletzt vergebenen Nummern im aktiven Projekt (Regel in `letzte-nummern.ts`) */
  letzteNummern: LetzteNummer[] = [];

  /** Punktart, Punkt und Projekt, fuer die die Liste zuletzt geladen wurde */
  private _letzteNummernFuer = '';

  override ngDoCheck(): void {
    super.ngDoCheck();
    if (!this.data) return;

    // Neu laden, sobald sich Punktart, Punkt oder Projekt aendern - nicht nur
    // die Punktart: Beim Wechsel auf einen anderen Punkt derselben Art blieb
    // sonst die Liste des vorigen stehen (samt dessen Nummer).
    const fuer = `${this.data.type || ''}|${this.data._id || ''}|${this.projectService.project()}`;
    if (fuer !== this._letzteNummernFuer) {
      this._letzteNummernFuer = fuer;
      this.loadLetzteNummern();
    }
  }

  /**
   * Punktart-Typeahead: zeigt/sucht das Label "Beschreibung (Nummer)" (Single
   * Source aus der Punktarten-Liste), speichert aber den type (Nummer). Unbekanntes
   * laesst den type unveraendert (Validierung markiert es als ungueltig).
   */
  get punktartModel(): string {
    return punktartDisplayLabel(this.data?.type);
  }
  set punktartModel(label: string) {
    const nummer = punktartNummerFromLabel(label);
    if (nummer) this.data.type = nummer;
  }

  /**
   * Punktart über den visuellen Picker gewählt: rohe Nummer setzen und die
   * Änderung als "dirty" melden (der Picker ist kein wd-*-Control und meldet
   * die Änderung daher selbst — analog generateNummer). So erscheint der
   * Speichern-Knopf wie bei den übrigen Feldern.
   */
  onPunktartPicked(type: string): void {
    this.data.type = type;
    this.vs.dirty.next(true);
  }

  onNummerChanged(data: { previous: string, current: string }) {
    this.ds.nummerChanged.emit({ id: this.data._id, num: data.current });
  }

  /** Projektzugehörigkeit als schlichter Text für die Zeile „Projekt": Name (Datum aus der Kennung), mehrere mit Komma. */
  projekteString(): string {
    const projects = this.data.header.projects;
    if (!projects || projects.length === 0) return 'Kein Projekt zugeordnet';
    return projects.map((p: { id: string, name: string }) => {
      const datum = this.parseProjectDate(p.id);
      return datum ? `${p.name} (${datum})` : p.name;
    }).join(', ');
  }

  parseProjectDate(projectId: string): string {
    if (!projectId || projectId.length < 8) return '';
    const datepart = projectId.substring(0, 8);
    if (!/^\d{8}$/.test(datepart)) return '';
    const year = datepart.substring(0, 4);
    const month = datepart.substring(4, 6);
    const day = datepart.substring(6, 8);
    return `${day}.${month}.${year}`;
  }

  /**
   * Darf der aktuelle User den Kartierer umhängen? Nur Owner/Entwickler — das
   * koppelt an das Punkt-Eigentum (workflow.createdBy) und ist eine Punktdaten-
   * Änderung (Manager dürfen das nicht, Bearbeiter sollen Eigentum nicht
   * verschieben). Sonst wird der Kartierer nur lesend gezeigt.
   */
  canChangeKartierer(): boolean {
    return this.projectService.canIReassignKartierer();
  }

  /** Auswahl-Optionen (Pipe-Liste) für das Kartierer-Dropdown = Projekt-Mitglieder (Anzeigenamen). */
  get kartiererOptions(): string {
    return kartiererOptionsFromProject(this.projectService.currentProjectDoc())
      .map(m => m.name)
      .join('|');
  }

  /**
   * Kartierer-Auswahl im Einzel-Formular: setzt Name UND Eigentum
   * (workflow.createdBy) gemeinsam und speichert sofort — analog removeProject,
   * also unabhängig vom allgemeinen Edit-Lock (eigene privilegierte Aktion).
   * Eine Auswahl ohne passendes Mitglied (z.B. Leereintrag) wird ignoriert, damit
   * das Eigentum nie an einen Nicht-Member kippt.
   */
  async onKartiererSelected(name: string): Promise<void> {
    const member = kartiererOptionsFromProject(this.projectService.currentProjectDoc())
      .find(m => m.name === name);
    if (!member) return;
    if (member.name === this.data.header.aufnahme?.kartierer
      && member.id === this.data.workflow?.createdBy?.id) {
      return; // keine Änderung
    }
    applyKartiererOwner(this.data, member);
    await this.ds.saveDocument(this.data);
  }

  /** Extrahiert die 2-stellige Nummer am Anfang eines Forstamt/Revier-Strings (z.B. "06 Neustrelitz" → "06") */
  private extractNr(value: string | undefined): string {
    if (!value) return '';
    const match = value.match(/^(\d{2})\s/);
    return match ? match[1] : '';
  }

  canGenerateNummer(): boolean {
    return !!this.extractNr(this.data.header.forstamt) && !!this.extractNr(this.data.header.revier);
  }

  /** Erzeugt Bohrpunktnummer: FA-FR-lfdNr-Jahr */
  generateNummer() {
    const fa = this.extractNr(this.data.header.forstamt);
    const fr = this.extractNr(this.data.header.revier);
    if (!fa || !fr) return;

    // Bestehende Nummer original übernehmen (auch Buchstaben)
    const lfdNr = this.data.header.nummer?.trim() || '1';
    const jahr = new Date().getFullYear();
    const newNummer = `${fa}-${fr}-${lfdNr}-${jahr}`;
    this.data.header.nummer = newNummer;
    this.vs.dirty.next(true);
    this.onNummerChanged({ previous: '', current: newNummer });
  }

  get nummernPopoverTitle(): string {
    const typ = idOf(this.data?.type) === 7 ? 'Moor-Nummern' : 'Nummern';
    const n = this.letzteNummern.length;
    return n > 0 ? `Meine letzten ${typ} (${n})` : `Meine letzten ${typ}`;
  }

  /**
   * Laedt meine zuletzt vergebenen Nummern im aktiven Projekt aus dem Index
   * (ein Aufruf gegen die lokale Datenbank; die Auswahlregel steht in
   * `letzte-nummern.ts`).
   */
  private async loadLetzteNummern() {
    this.letzteNummern = [];

    if (!this.data?.type) return;
    if (!istPunktartMitProtokoll(this.data.type)) return;

    try {
      const response = await this.ds.queryIndex(false);
      this.letzteNummern = letzteNummernAusIndex(response?.rows, {
        eigenePunktId: this.data._id,
        punktart: this.data.type,
        projektId: this.projectService.project(),
        nutzerId: this.auth.currentUserId,
      });
    } catch (err) {
      console.warn('Fehler beim Laden der letzten Nummern:', err);
    }
  }
}
